import React from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { Box, Heading, HStack, VStack, Button, Select, Input, Table, Thead, Tbody, Tr, Th, Td, Text, Badge, IconButton, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure, FormControl, FormLabel, NumberInput, NumberInputField, Switch, Tooltip } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminListComments, adminUpdateCommentStatus, adminBanUser, adminListUnbanRequests, adminResolveUnban, adminListBans, adminLiftBan } from '../../services/admin/comments';
import { deleteComment } from '../../services/comments';
import { FiTrash2, FiEye, FiEyeOff } from 'react-icons/fi';
import { getArticles } from '../../services/articles';
import { getEvents } from '../../services/eventService';
import { getCachedYouTube } from '../../services/youtube';
import api from '../../services/api';
import { adminListUsers } from '../../services/admin/engagement';

const CommentsAdminPage: React.FC = () => {
  const [status, setStatus] = React.useState<string>('');
  const [targetType, setTargetType] = React.useState<string>('');
  const [targetId, setTargetId] = React.useState<string>('');
  const [userId, setUserId] = React.useState<string>('');
  const [page, setPage] = React.useState<number>(1);
  const [reportedOnly, setReportedOnly] = React.useState<boolean>(false);
  const toast = useToast();
  const qc = useQueryClient();

  const [targetOptions, setTargetOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const [targetLoading, setTargetLoading] = React.useState<boolean>(false);
  const [userOptions, setUserOptions] = React.useState<Array<{ value: string; label: string }>>([]);
  const [userLoading, setUserLoading] = React.useState<boolean>(false);

  const listQ = useQuery({
    queryKey: ['admin-comments', { status, targetType, targetId, userId, page }],
    queryFn: () => adminListComments({ status: status as any, target_type: targetType, target_id: targetId, user_id: userId, page, page_size: 50 }),
    keepPreviousData: true,
  });

  const unbanQ = useQuery({
    queryKey: ['admin-unban-requests'],
    queryFn: adminListUnbanRequests,
  });

  const bansQ = useQuery({
    queryKey: ['admin-comment-bans'],
    queryFn: adminListBans,
  });

  const updateStatusMut = useMutation({
    mutationFn: (args: { id: number; s: 'visible'|'hidden' }) => adminUpdateCommentStatus(args.id, args.s),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin-comments'] }); },
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteComment(id),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin-comments'] }); toast({ status: 'success', title: 'Smazáno' }); },
  });

  const [banUserId, setBanUserId] = React.useState<number | null>(null);
  const banModal = useDisclosure();
  const [banReason, setBanReason] = React.useState<string>('Porušení pravidel diskuse');
  const [banHours, setBanHours] = React.useState<number>(0);
  const banMut = useMutation({
    mutationFn: () => adminBanUser(banUserId || 0, banReason, banHours),
    onSuccess: async () => { banModal.onClose(); setBanUserId(null); toast({ status: 'success', title: 'Uživatel zablokován' }); },
  });

  const resolveUnbanMut = useMutation({
    mutationFn: (args: { id: number; action: 'approve'|'reject' }) => adminResolveUnban(args.id, args.action),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin-unban-requests'] });
      await qc.invalidateQueries({ queryKey: ['admin-comment-bans'] });
      toast({ status: 'success', title: 'Vyřízeno' });
    },
  });

  const liftBanMut = useMutation({
    mutationFn: (id: number) => adminLiftBan(id),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ['admin-comment-bans'] }); toast({ status: 'success', title: 'Ban zrušen' }); },
  });

  React.useEffect(() => {
    const load = async () => {
      if (!targetType) { setTargetOptions([]); return; }
      try {
        setTargetLoading(true);
        if (targetType === 'article') {
          const res = await getArticles({ page: 1, page_size: 100 });
          setTargetOptions((res.data || []).map((a: any) => ({ value: String(a.id), label: `${a.title} (#${a.id})` })));
        } else if (targetType === 'event') {
          const res = await getEvents();
          setTargetOptions((res || []).map((e: any) => ({ value: String(e.id), label: `${e.title} (#${e.id})` })));
        } else if (targetType === 'gallery_album') {
          const r = await api.get('/gallery/albums');
          const arr = Array.isArray(r.data) ? r.data : (r.data?.data || r.data?.albums || []);
          setTargetOptions((arr || []).map((al: any) => ({ value: String(al.id), label: `${al.title} (${al.date || ''})` })));
        } else if (targetType === 'youtube_video') {
          const yt = await getCachedYouTube();
          const vids = yt?.videos || [];
          setTargetOptions(vids.map((v: any) => ({ value: String(v.video_id), label: `${v.title} (${v.published_date || ''})` })));
        } else {
          setTargetOptions([]);
        }
      } catch (e: any) {
        setTargetOptions([]);
      } finally {
        setTargetLoading(false);
      }
    };
    load();
  }, [targetType]);

  React.useEffect(() => {
    const loadUsers = async () => {
      try {
        setUserLoading(true);
        const users = await adminListUsers();
        setUserOptions((users || []).map((u: any) => ({ value: String(u.id), label: `${u.name || u.email} (#${u.id})` })));
      } catch {
        setUserOptions([]);
      } finally {
        setUserLoading(false);
      }
    };
    loadUsers();
  }, []);

  const itemsAll = listQ.data?.items || [];
  const items = React.useMemo(() => {
    if (!reportedOnly) return itemsAll;
    return itemsAll.filter((c: any) => (c as any).reports && (c as any).reports > 0);
  }, [itemsAll, reportedOnly]);

  return (
    <AdminLayout>
      <Box>
        <Heading size="md" mb={4}>Komentáře (moderace)</Heading>
        <VStack align="stretch" spacing={3} mb={4}>
          <HStack>
            <Select placeholder="Stav" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} maxW="200px">
              <option value="visible">Viditelné</option>
              <option value="hidden">Skryté</option>
            </Select>
            <Select placeholder="Typ cíle" value={targetType} onChange={(e) => { setTargetType(e.target.value); setPage(1); setTargetId(''); }} maxW="220px">
              <option value="article">Článek</option>
              <option value="event">Aktivita</option>
              <option value="gallery_album">Galerie</option>
              <option value="youtube_video">YouTube video</option>
            </Select>
            {targetType && (
              <Select placeholder={targetLoading ? 'Načítání…' : 'Cíl'} value={targetId} onChange={(e) => { setTargetId(e.target.value); setPage(1); }} maxW="320px" isDisabled={targetLoading}>
                {targetOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            )}
            <Select placeholder={userLoading ? 'Načítání uživatelů…' : 'Uživatel'} value={userId} onChange={(e) => { setUserId(e.target.value); setPage(1); }} maxW="260px" isDisabled={userLoading}>
              {userOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
            <HStack>
              <Text fontSize="sm" color="gray.500">Jen nahlášené</Text>
              <Switch isChecked={reportedOnly} onChange={(e)=>setReportedOnly(e.target.checked)} />
            </HStack>
            <Button size="sm" variant="ghost" onClick={() => { setStatus(''); setTargetType(''); setTargetId(''); setUserId(''); setReportedOnly(false); setPage(1); }}>Reset</Button>
          </HStack>
        </VStack>

        <Box borderWidth="1px" borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Uživatel</Th>
                <Th>Cíl</Th>
                <Th>Obsah</Th>
                <Th>Spam</Th>
                <Th>Hlášení</Th>
                <Th>Stav</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((c) => (
                <Tr key={c.id}>
                  <Td>#{c.id}</Td>
                  <Td>#{c.user?.id} {c.user?.first_name} {c.user?.last_name}</Td>
                  <Td>
                    <Badge mr={2}>{c.target_type}</Badge>
                    <Text as="span">{c.target_label || c.target_id}</Text>
                  </Td>
                  <Td maxW="420px"><Text noOfLines={2}>{c.content}</Text></Td>
                  <Td>{(c as any).spam_score ? <Badge colorScheme={(c as any).spam_score > 0.5 ? 'orange' : 'green'}>{(c as any).spam_score.toFixed(2)}</Badge> : '-'}</Td>
                  <Td>{(c as any).reports ? <Badge colorScheme={(c as any).reports > 2 ? 'red' : 'yellow'}>{(c as any).reports}</Badge> : '-'}</Td>
                  <Td>
                    <Tooltip label={c.status === 'visible' ? 'Viditelné' : 'Skryté'}>
                      <IconButton
                        aria-label={c.status === 'visible' ? 'Viditelné' : 'Skryté'}
                        size="xs"
                        variant="ghost"
                        colorScheme={c.status === 'visible' ? 'green' : 'gray'}
                        icon={c.status === 'visible' ? <FiEye /> : <FiEyeOff />}
                        onClick={() => updateStatusMut.mutate({ id: c.id, s: (c.status === 'visible' ? 'hidden' : 'visible') as any })}
                      />
                    </Tooltip>
                  </Td>
                  <Td>
                    <HStack>
                      <IconButton aria-label="Smazat" size="xs" icon={<FiTrash2 />} onClick={() => deleteMut.mutate(c.id)} />
                      <Button size="xs" variant="outline" onClick={() => { setBanUserId(c.user?.id as any); banModal.onOpen(); }}>Ban</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <HStack mt={3} justify="space-between">
          <Text fontSize="sm" color="gray.500">Stránka {page} • {listQ.data?.total || 0} komentářů</Text>
          <HStack>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>Předchozí</Button>
            <Button size="sm" variant="outline" onClick={() => setPage(p => p + 1)} isDisabled={(itemsAll.length === 0) || ((itemsAll.length < 50) && (listQ.data?.total || 0) <= (page * 50))}>Další</Button>
          </HStack>
        </HStack>

        <Heading size="sm" mt={6} mb={2}>Žádosti o odblokování</Heading>
        <Box borderWidth="1px" borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Uživatel</Th>
                <Th>Text</Th>
                <Th>Stav</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(unbanQ.data?.items || []).map((r) => (
                <Tr key={r.id}>
                  <Td>#{r.id}</Td>
                  <Td>#{r.user?.id} {r.user?.first_name} {r.user?.last_name} <Text as="span" color="gray.500" fontSize="sm">{r.user?.email}</Text></Td>
                  <Td maxW="480px"><Text noOfLines={2}>{r.message}</Text></Td>
                  <Td>
                    {r.status === 'pending' && <Badge colorScheme="yellow">Čeká na vyřízení</Badge>}
                    {r.status === 'approved' && <Badge colorScheme="green">Schváleno</Badge>}
                    {r.status === 'rejected' && <Badge colorScheme="red">Zamítnuto</Badge>}
                  </Td>
                  <Td>
                    <HStack>
                      <Button size="xs" colorScheme="green" variant="outline" onClick={() => resolveUnbanMut.mutate({ id: r.id, action: 'approve' })}>Povolit</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => resolveUnbanMut.mutate({ id: r.id, action: 'reject' })}>Zamítnout</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <Heading size="sm" mt={6} mb={2}>Zablokovaní uživatelé</Heading>
        <Box borderWidth="1px" borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>Uživatel</Th>
                <Th>Důvod</Th>
                <Th>Zabanován</Th>
                <Th>Platné do</Th>
                <Th>Akce</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(bansQ.data?.items || []).map((b) => {
                const untilText = !b.until ? 'Trvale' : new Date(b.until).toLocaleString();
                return (
                  <Tr key={b.id}>
                    <Td>#{b.id}</Td>
                    <Td>#{b.user?.id} {b.user?.first_name} {b.user?.last_name} <Text as="span" color="gray.500" fontSize="sm">{b.user?.email}</Text></Td>
                    <Td>{b.reason || '-'}</Td>
                    <Td>{new Date(b.created_at).toLocaleString()}</Td>
                    <Td>{untilText}</Td>
                    <Td>
                      <Button size="xs" variant="outline" onClick={() => liftBanMut.mutate(b.id)}>Zrušit ban</Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        {/* Ban modal */}
        <Modal isOpen={banModal.isOpen} onClose={banModal.onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Zablokovat uživatele #{banUserId}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="stretch" spacing={3}>
                <FormControl>
                  <FormLabel>Důvod</FormLabel>
                  <Input value={banReason} onChange={(e) => setBanReason(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Doba (hodiny) – 0 = trvale</FormLabel>
                  <NumberInput min={0} value={banHours} onChange={(v) => setBanHours(Number(v) || 0)}>
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
                <HStack>
                  <Text fontSize="sm" color="gray.500">Rychlá volba:</Text>
                  <Button size="xs" variant="outline" onClick={()=>setBanHours(24)}>24h</Button>
                  <Button size="xs" variant="outline" onClick={()=>setBanHours(24*7)}>7 dní</Button>
                  <Button size="xs" variant="outline" onClick={()=>setBanHours(0)}>Trvale</Button>
                </HStack>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button onClick={banModal.onClose}>Zrušit</Button>
                <Button colorScheme="red" isLoading={banMut.isPending} onClick={() => banMut.mutate()}>Zablokovat</Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </AdminLayout>
  );
};

export default CommentsAdminPage;
